import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Printer, FileDown, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import {
  buildOfficialVolantePrintHtml,
  buildGameListPrintHtml,
  buildGameListGroupedPrintHtml,
  generateGameListPDF,
  generateGameListGroupedPDF,
  generateOfficialVolantePDF,
  getOfficialVolantePageCount,
  OFFICIAL_VOLANTE_LAYOUTS,
  type ListaGroup,
} from "@/lib/volante-pdf";

type Slug = "mega-sena" | "lotofacil" | "quina" | "dupla-sena";
type Mode = "list" | "official";
type TrayPos = "side" | "center";
/** Deslocamento lateral → centralizado: metade da diferença A4 (210mm) - volante oficial (83mm). */
const CENTER_TRAY_OFFSET_MM = 63.5;

interface Props {
  lotterySlug: Slug;
  lotteryName: string;
  games: number[][];
  brandLine?: string;
  costPerGame?: number;
  filenameBase?: string;
  trigger?: React.ReactNode;
  disabled?: boolean;
  /**
   * Optional multi-lottery selection (used in "Meus jogos").
   * If provided, "Tabela (lista)" mode prints one PDF grouped by lottery.
   * "Volante oficial" mode requires a single lottery and uses `games` / `lotterySlug`.
   */
  groups?: ListaGroup[];
}

export function ExportGamesDialog({
  lotterySlug,
  lotteryName,
  games,
  brandLine = "LotoTerminal",
  costPerGame,
  filenameBase,
  trigger,
  disabled,
  groups,
}: Props) {
  const [open, setOpen] = useState(false);
  const isMultiLottery = !!groups && groups.length > 1;
  const [mode, setMode] = useState<Mode>("list");
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [trayPos, setTrayPos] = useState<TrayPos>("side");

  const layout = OFFICIAL_VOLANTE_LAYOUTS[lotterySlug];
  const officialPageCount = getOfficialVolantePageCount(lotterySlug, games.length);

  const pageOffsetX = trayPos === "center" ? CENTER_TRAY_OFFSET_MM : 0;

  const baseName = filenameBase ?? `jogos-${lotterySlug}`;
  const today = new Date().toISOString().slice(0, 10);
  const totalGames = groups ? groups.reduce((a, g) => a + g.games.length, 0) : games.length;

  const buildDoc = () => {
    if (mode === "list") {
      if (groups && groups.length > 0) {
        return {
          doc: generateGameListGroupedPDF({ groups, brandLine }),
          filename: `${baseName}-${today}.pdf`,
        };
      }
      return {
        doc: generateGameListPDF({ lotteryName, games, costPerGame, brandLine }),
        filename: `${baseName}-${today}.pdf`,
      };
    }
    return {
      doc: generateOfficialVolantePDF({ lotterySlug, games, pageOffsetX, offsetX, offsetY }),
      filename: `${baseName}-volante-${today}.pdf`,
    };
  };

  const handleSave = () => {
    if (totalGames === 0) return toast.error("Nenhum jogo para exportar");
    if (mode === "official" && isMultiLottery) {
      return toast.error("Selecione jogos de uma única loteria para imprimir no volante oficial.");
    }
    const { doc, filename } = buildDoc();
    doc.save(filename);
    toast.success(mode === "list" ? "Lista exportada" : `${officialPageCount} volante(s) gerado(s)`);
    setOpen(false);
  };

  const handlePrint = () => {
    if (totalGames === 0) return toast.error("Nenhum jogo para imprimir");
    if (mode === "official" && isMultiLottery) {
      return toast.error("Selecione jogos de uma única loteria para imprimir no volante oficial.");
    }
    const html =
      mode === "official"
        ? buildOfficialVolantePrintHtml({ lotterySlug, games, pageOffsetX, offsetX, offsetY })
        : groups && groups.length > 0
          ? buildGameListGroupedPrintHtml({ groups, brandLine })
          : buildGameListPrintHtml({ lotteryName, games, costPerGame, brandLine });
    const existing = document.getElementById("loto-print-frame");
    if (existing) existing.remove();
    const iframe = document.createElement("iframe");
    iframe.id = "loto-print-frame";
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
    const printDoc = iframe.contentWindow?.document;
    if (!printDoc) {
      const { doc, filename } = buildDoc();
      doc.save(filename);
      toast.message("Impressão direta bloqueada — PDF salvo. Abra e imprima manualmente.");
      return;
    }
    printDoc.open();
    printDoc.write(html);
    printDoc.close();
    if (mode === "official") {
      toast.success(`Imprimindo ${officialPageCount} volante(s) no tamanho oficial — escala 100%, sem margens.`);
    } else {
      toast.success("Abrindo diálogo de impressão");
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline" disabled={disabled || totalGames === 0}>
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Exportar / Imprimir
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif">
            Exportar {totalGames} jogo(s) — {isMultiLottery ? `${groups!.length} loterias` : lotteryName}
          </DialogTitle>
        </DialogHeader>

        <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="exp-list"
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
              mode === "list" ? "border-primary bg-primary/5" : "border-lt-border"
            }`}
          >
            <RadioGroupItem value="list" id="exp-list" className="mt-0.5" />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <ListOrdered className="h-4 w-4 text-primary" /> Tabela (lista)
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                PDF compacto com todos os jogos para conferência{isMultiLottery ? ", separados por loteria" : ""}.
              </p>
            </div>
          </label>
          <label
            htmlFor="exp-official"
            className={`flex items-start gap-3 rounded-md border p-3 transition-colors ${
              isMultiLottery ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            } ${mode === "official" ? "border-primary bg-primary/5" : "border-lt-border"}`}
          >
            <RadioGroupItem value="official" id="exp-official" className="mt-0.5" disabled={isMultiLottery} />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <Printer className="h-4 w-4 text-primary" /> Volante oficial
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {isMultiLottery
                  ? "Selecione jogos de uma única loteria para usar o volante oficial."
                  : `Marca direto no volante físico — até ${layout.playsPerSlip} jogos por volante.`}
              </p>
            </div>
          </label>
        </RadioGroup>

        {mode === "official" && !isMultiLottery && (
          <>
            <Alert className="border-primary/30 bg-primary/5">
              <Printer className="h-4 w-4 text-primary" />
              <AlertTitle>Antes de imprimir</AlertTitle>
              <AlertDescription className="text-xs leading-relaxed">
                Coloque {officialPageCount} volante{officialPageCount !== 1 ? "s" : ""} oficial{officialPageCount !== 1 ? "is" : ""} de {lotteryName} em branco na impressora.
                Imprima em <strong>A4 retrato, escala 100% (sem ajuste) e margens 0</strong>, escolhendo abaixo se a bandeja encosta o volante na lateral ou o posiciona centralizado.
                O alinhamento já vem calibrado — use os ajustes abaixo só se sua impressora desviar alguns mm.
              </AlertDescription>
            </Alert>
            <div>
              <Label className="text-xs text-muted-foreground">Como sua impressora posiciona o volante na bandeja?</Label>
              <RadioGroup
                value={trayPos}
                onValueChange={(v) => setTrayPos(v as TrayPos)}
                className="mt-2 grid gap-2 sm:grid-cols-2"
              >
                <label
                  htmlFor="tray-side"
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-xs transition-colors ${
                    trayPos === "side" ? "border-primary bg-primary/5" : "border-lt-border"
                  }`}
                >
                  <RadioGroupItem value="side" id="tray-side" className="mt-0.5" />
                  <div>
                    <div className="font-medium">Na lateral (encostado à esquerda)</div>
                    <p className="mt-0.5 text-muted-foreground">Volante alinhado à borda esquerda da bandeja.</p>
                  </div>
                </label>
                <label
                  htmlFor="tray-center"
                  className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 text-xs transition-colors ${
                    trayPos === "center" ? "border-primary bg-primary/5" : "border-lt-border"
                  }`}
                >
                  <RadioGroupItem value="center" id="tray-center" className="mt-0.5" />
                  <div>
                    <div className="font-medium">Centralizado na bandeja</div>
                    <p className="mt-0.5 text-muted-foreground">Guias da bandeja centralizam papéis menores (mais comum hoje).</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Ajuste horizontal (mm)</Label>
                <Input type="number" step={0.5} value={offsetX} onChange={(e) => setOffsetX(Number(e.target.value) || 0)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Ajuste vertical (mm)</Label>
                <Input type="number" step={0.5} value={offsetY} onChange={(e) => setOffsetY(Number(e.target.value) || 0)} className="mt-1" />
              </div>
            </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleSave}>
            <FileDown className="mr-2 h-4 w-4" /> Salvar PDF
          </Button>
          <Button onClick={handlePrint} className="bg-primary text-primary-foreground hover:opacity-90">
            <Printer className="mr-2 h-4 w-4" /> Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}